import React from "react";
import { useTranslation } from "react-i18next";
import styles from "../CRMSettingsPage.module.css";

function HelpHint({ title, text }) {
  return (
    <span
      title={text}
      aria-label={title}
      style={{
        marginLeft: 6,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        borderRadius: "999px",
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(255,255,255,0.05)",
        color: "rgba(255,255,255,0.72)",
        fontSize: 10,
        fontWeight: 900,
        cursor: "help",
        userSelect: "none",
        verticalAlign: "middle",
      }}
    >
      ?
    </span>
  );
}

export default function AITab({
  aiMode,
  setAiMode,
  aiFallbackEnabled,
  setAiFallbackEnabled,
  aiLogOutputs,
  setAiLogOutputs,
  aiContactSummaryEnabled,
  setAiContactSummaryEnabled,
  aiEmailWriterEnabled,
  setAiEmailWriterEnabled,
  aiLeadScoringEnabled,
  setAiLeadScoringEnabled,
  aiNextStepEnabled,
  setAiNextStepEnabled,
  aiOpenaiApiKey,
  setAiOpenaiApiKey,
  aiOpenaiModel,
  setAiOpenaiModel,
  aiOpenaiTemperature,
  setAiOpenaiTemperature,
  aiOpenaiMaxTokens,
  setAiOpenaiMaxTokens,
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.emailShell}>
      <div className={styles.emailHeader}>
        <div>
          <div
            className={styles.emailTitle}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            {t("crm_settings_tab_ai", { defaultValue: "AI" })}
            <HelpHint
              title={t("crm_settings_ai_settings_title", {
                defaultValue: "AI settings",
              })}
              text={t("crm_settings_ai_settings_help", {
                defaultValue: "Configure tenant AI behavior and provider.",
              })}
            />
          </div>
          <div className={styles.emailHint}>
            {t("crm_settings_ai_subtitle", {
              defaultValue: "AI features and provider configuration.",
            })}
          </div>
        </div>
      </div>

      <div className={styles.emailGrid2}>
        <div className={styles.emailCard}>
          <div className={styles.emailCardHead}>
            <div className={styles.emailCardTitle}>
              {t("crm_settings_email_general", {
                defaultValue: "General",
              })}
            </div>
            <span className={styles.pill}>
              {t("crm_settings_tenant", { defaultValue: "Tenant" })}
            </span>
          </div>

          <div className={styles.formCol}>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div
                  className={styles.fLabel}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  {t("crm_settings_ai_mode", {
                    defaultValue: "AI mode",
                  })}
                  <HelpHint
                    title={t("crm_settings_ai_mode", {
                      defaultValue: "AI mode",
                    })}
                    text={t("crm_settings_ai_mode_help", {
                      defaultValue:
                        "off = disabled, basic = internal logic, openai = external provider",
                    })}
                  />
                </div>
                <div className={styles.fHelp}>off / basic / openai</div>
              </div>
              <select
                className={styles.ctrl}
                value={aiMode}
                onChange={(e) => setAiMode(e.target.value)}
              >
                <option value="off">
                  {t("crm_settings_ai_mode_off", {
                    defaultValue: "Off",
                  })}
                </option>
                <option value="basic">
                  {t("crm_settings_ai_mode_basic", {
                    defaultValue: "Basic",
                  })}
                </option>
                <option value="openai">
                  {t("crm_settings_ai_mode_openai", {
                    defaultValue: "OpenAI",
                  })}
                </option>
              </select>
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div
                  className={styles.fLabel}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  {t("crm_settings_ai_fallback_enabled", {
                    defaultValue: "Fallback enabled",
                  })}
                  <HelpHint
                    title={t("crm_settings_ai_fallback_enabled", {
                      defaultValue: "Fallback enabled",
                    })}
                    text={t("crm_settings_ai_fallback_help", {
                      defaultValue: "Use fallback behavior if provider fails.",
                    })}
                  />
                </div>
                <div className={styles.fHelp}>
                  {t("crm_settings_ai_fallback_short", {
                    defaultValue: "Fallback",
                  })}
                </div>
              </div>
              <div className={styles.toggle}>
                <div className={styles.toggleLeft}>
                  <input
                    type="checkbox"
                    checked={aiFallbackEnabled}
                    onChange={(e) => setAiFallbackEnabled(e.target.checked)}
                  />
                  <span
                    className={styles.fLabel}
                    style={{ opacity: 0.9, fontWeight: 700 }}
                  >
                    {aiFallbackEnabled
                      ? t("crm_settings_on", { defaultValue: "On" })
                      : t("crm_settings_off", { defaultValue: "Off" })}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div
                  className={styles.fLabel}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  {t("crm_settings_ai_log_outputs", {
                    defaultValue: "Log outputs",
                  })}
                  <HelpHint
                    title={t("crm_settings_ai_log_outputs", {
                      defaultValue: "Log outputs",
                    })}
                    text={t("crm_settings_ai_log_outputs_help", {
                      defaultValue:
                        "Store AI outputs for later troubleshooting.",
                    })}
                  />
                </div>
                <div className={styles.fHelp}>
                  {t("crm_settings_optional", {
                    defaultValue: "Optional",
                  })}
                </div>
              </div>
              <div className={styles.toggle}>
                <div className={styles.toggleLeft}>
                  <input
                    type="checkbox"
                    checked={aiLogOutputs}
                    onChange={(e) => setAiLogOutputs(e.target.checked)}
                  />
                  <span
                    className={styles.fLabel}
                    style={{ opacity: 0.9, fontWeight: 700 }}
                  >
                    {aiLogOutputs
                      ? t("crm_settings_on", { defaultValue: "On" })
                      : t("crm_settings_off", { defaultValue: "Off" })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.emailCard}>
          <div className={styles.emailCardHead}>
            <div className={styles.emailCardTitle}>
              {t("crm_settings_ai_features", {
                defaultValue: "AI features",
              })}
            </div>
            <span className={styles.pill}>
              {t("crm_settings_switches", {
                defaultValue: "Switches",
              })}
            </span>
          </div>

          <div className={styles.formCol}>
            <div className={styles.toggle}>
              <div className={styles.toggleLeft}>
                <input
                  type="checkbox"
                  checked={aiContactSummaryEnabled}
                  onChange={(e) => setAiContactSummaryEnabled(e.target.checked)}
                />
                <span className={styles.fLabel}>
                  {t("crm_settings_ai_feature_contact_summary", {
                    defaultValue: "Contact summary",
                  })}
                </span>
              </div>
            </div>

            <div className={styles.toggle}>
              <div className={styles.toggleLeft}>
                <input
                  type="checkbox"
                  checked={aiEmailWriterEnabled}
                  onChange={(e) => setAiEmailWriterEnabled(e.target.checked)}
                />
                <span className={styles.fLabel}>
                  {t("crm_settings_ai_feature_email_writer", {
                    defaultValue: "Email writer",
                  })}
                </span>
              </div>
            </div>

            <div className={styles.toggle}>
              <div className={styles.toggleLeft}>
                <input
                  type="checkbox"
                  checked={aiLeadScoringEnabled}
                  onChange={(e) => setAiLeadScoringEnabled(e.target.checked)}
                />
                <span className={styles.fLabel}>
                  {t("crm_settings_ai_feature_lead_scoring", {
                    defaultValue: "Lead scoring",
                  })}
                </span>
              </div>
            </div>

            <div className={styles.toggle}>
              <div className={styles.toggleLeft}>
                <input
                  type="checkbox"
                  checked={aiNextStepEnabled}
                  onChange={(e) => setAiNextStepEnabled(e.target.checked)}
                />
                <span className={styles.fLabel}>
                  {t("crm_settings_ai_feature_next_step", {
                    defaultValue: "Next step suggestion",
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {aiMode === "openai" && (
        <div className={styles.emailCard} style={{ marginTop: 12 }}>
          <div className={styles.emailCardHead}>
            <div className={styles.emailCardTitle}>
              {t("crm_settings_ai_openai_provider", {
                defaultValue: "OpenAI provider",
              })}
            </div>
            <span className={styles.pill}>
              {t("crm_settings_provider", {
                defaultValue: "Provider",
              })}
            </span>
          </div>

          <div className={styles.formCol}>
            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div className={styles.fLabel}>
                  {t("crm_settings_ai_api_key", {
                    defaultValue: "API key",
                  })}
                </div>
                <div className={styles.fHelp}>
                  {t("crm_settings_ai_api_key_hint", {
                    defaultValue: "Stored per tenant",
                  })}
                </div>
              </div>
              <input
                className={styles.ctrl}
                type="password"
                value={aiOpenaiApiKey}
                onChange={(e) => setAiOpenaiApiKey(e.target.value)}
                placeholder="sk-..."
              />
            </div>

            <div className={styles.row2}>
              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <div className={styles.fLabel}>
                    {t("crm_settings_ai_model", {
                      defaultValue: "Model",
                    })}
                  </div>
                </div>
                <input
                  className={styles.ctrl}
                  value={aiOpenaiModel}
                  onChange={(e) => setAiOpenaiModel(e.target.value)}
                  placeholder="gpt-4o-mini"
                />
              </div>

              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <div className={styles.fLabel}>
                    {t("crm_settings_ai_temperature", {
                      defaultValue: "Temperature",
                    })}
                  </div>
                </div>
                <input
                  className={styles.ctrl}
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={aiOpenaiTemperature}
                  onChange={(e) => setAiOpenaiTemperature(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <div className={styles.fLabel}>
                  {t("crm_settings_ai_max_tokens", {
                    defaultValue: "Max tokens",
                  })}
                </div>
              </div>
              <input
                className={styles.ctrl}
                type="number"
                min="1"
                value={aiOpenaiMaxTokens}
                onChange={(e) => setAiOpenaiMaxTokens(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      <div
        className={styles.cardSub}
        style={{
          marginTop: 14,
          padding: "14px 16px",
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.03)",
        }}
      >
        {t("crm_settings_ai_recommendation_prefix", {
          defaultValue: "Recommended to start with",
        })}{" "}
        <strong>
          {t("crm_settings_ai_mode_basic", {
            defaultValue: "Basic",
          })}
        </strong>{" "}
        {t("crm_settings_ai_recommendation_middle", {
          defaultValue: "and switch to",
        })}{" "}
        <strong>
          {t("crm_settings_ai_mode_openai", {
            defaultValue: "OpenAI",
          })}
        </strong>{" "}
        {t("crm_settings_ai_recommendation_suffix", {
          defaultValue: "when tenant credentials are ready.",
        })}
      </div>
    </div>
  );
}